import { C } from '../lib/theme';

// #516 — how a case's owning customer is shown, in one place.
//
// Two surfaces render this (the Cases list and the case-detail header) and they
// have to agree, because the thing they are distinguishing is which COMPANY a
// registration belongs to. Two spellings of "not linked" across two screens is
// how somebody concludes the blank one is still loading.
//
// NOT LINKED IS AN EXPLICIT STATE, not an empty cell. Every case created before
// #514 is un-linked, so this is the common rendering, not the edge case — and a
// blank cell reads as "loading", or as "this screen is broken", neither of which
// is true. It says what is true: nothing owns this case yet.
//
// NAME WITH THE ID UNDER IT, rather than either alone. The name is what a
// person recognises; the id is what the API, the RLS policy and every other
// staff screen use, so hiding it would make this the one place you cannot
// cross-reference. When the directory has not loaded (or the caller could not
// read it) the id stands alone — degraded, never blank, and never wrong.

export interface CustomerLabelProps {
  customerId: string | null | undefined;
  /** The resolved company name, or null when unknown — see useCustomerDirectory. */
  name: string | null;
  /** `chip` for the case header, `cell` for a table row. */
  variant?: 'chip' | 'cell';
}

export function CustomerLabel({ customerId, name, variant = 'cell' }: CustomerLabelProps): JSX.Element {
  if (!customerId) {
    return (
      <span
        data-testid="customer-unlinked"
        style={{ fontSize: 12, color: C.textDim, fontStyle: 'italic' }}
      >
        Not linked
      </span>
    );
  }

  if (variant === 'chip') {
    return (
      <span
        data-testid="customer-label"
        title={customerId}
        style={{
          fontSize: 11,
          color: C.textMuted,
          background: C.goldBg,
          padding: '3px 8px',
          borderRadius: 4,
          fontWeight: 600,
        }}
      >
        {name ?? <span style={{ fontFamily: 'monospace' }}>{customerId}</span>}
      </span>
    );
  }

  return (
    <span data-testid="customer-label" title={customerId} style={{ fontSize: 12, color: C.textMuted }}>
      {name ?? <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{customerId}</span>}
    </span>
  );
}

/**
 * The owning customer id of a case, as TEXT — the same value Postgres's `->>`
 * produces, which is what the RLS policy actually matches on.
 *
 * Not `typeof value === 'string'`, and the difference is visible to a user.
 * `onboarding` was unvalidated for most of its life, so a case can carry a JSON
 * NUMBER here; `->>` turns it into a perfectly good tenant string, so that case
 * IS linked and its customer CAN read it. A string-only check would render it
 * "Not linked" with a live Link customer button beside it — an invitation to
 * perform a move the server refuses with a 409. Mirrors customerIdAsText in
 * routes/cases.ts; the empty string is not a link on either side.
 */
export function caseCustomerId(c: { onboarding?: unknown }): string | null {
  const onboarding = c.onboarding;
  if (!onboarding || typeof onboarding !== 'object' || Array.isArray(onboarding)) return null;
  const value = (onboarding as Record<string, unknown>).customerId;
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value.length > 0 ? value : null;
  return typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : JSON.stringify(value);
}
